import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  Checkbox,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  TextField,
  Grid,
  Snackbar,
  Alert,
  useTheme,
  CircularProgress
} from '@mui/material';
import { Visibility as ViewIcon, FileDownload, CheckCircle as ResolveIcon, DoNotDisturbOn as CloseIcon, Delete as DeleteIcon } from '@mui/icons-material';
import contactService from '../../services/contactService';
import adminService from '../../services/adminService';
import { ButtonPill, panelSx } from '../ui';

const ContactManagement = () => {
  const theme = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [selectedContact, setSelectedContact] = useState(null);
  const [openDialog, setOpenDialog] = useState(false);
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [totalCount, setTotalCount] = useState(0);
  const [selected, setSelected] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  const [confirmDeleteOpen, setConfirmDeleteOpen] = useState(false);
  const [exporting, setExporting] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });

  const fetchContacts = async () => {
    try {
      setLoading(true);
      const res = await contactService.getAllContacts({ page: page + 1, per_page: rowsPerPage });
      const list = res?.contacts ?? res?.data?.contacts ?? [];
      setContacts(list);
      setTotalCount(res?.pagination?.total_count ?? res?.pagination?.total ?? list.length);
    } catch (err) {
      console.error('Error fetching contacts:', err);
      setContacts([]);
      setTotalCount(0);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchContacts();
    setSelected([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, rowsPerPage]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'new': return 'error';
      case 'in_progress': return 'warning';
      case 'resolved': return 'success';
      default: return 'default';
    }
  };

  const handleViewDetails = (contact) => {
    setSelectedContact(contact);
    setOpenDialog(true);
  };

  const toggleSelect = (id) => {
    setSelected(prev => prev.includes(id) ? prev.filter(x => x !== id) : [...prev, id]);
  };

  const handleSelectAll = (event) => {
    setSelected(event.target.checked ? contacts.map(c => c.id) : []);
  };

  const handleExport = async () => {
    try {
      setExporting(true);
      await adminService.exportCsv('contacts');
      setSnackbar({ open: true, message: 'Export started', severity: 'success' });
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Export failed', severity: 'error' });
    } finally {
      setExporting(false);
    }
  };

  const runBulk = async (action, value) => {
    if (selected.length === 0) return;
    try {
      setBulkLoading(true);
      const payload = { ids: selected, action };
      if (value !== undefined) payload.value = value;
      const res = await adminService.bulkAction('contacts', payload);
      const label = action === 'delete' ? 'deleted' : `marked ${value}`;
      setSnackbar({ open: true, message: `${res.affected ?? selected.length} contact(s) ${label}`, severity: 'success' });
      setSelected([]);
      await fetchContacts();
    } catch (err) {
      setSnackbar({ open: true, message: err?.message ?? 'Bulk action failed', severity: 'error' });
    } finally {
      setBulkLoading(false);
      setConfirmDeleteOpen(false);
    }
  };

  const allSelected = contacts.length > 0 && selected.length === contacts.length;
  const someSelected = selected.length > 0 && selected.length < contacts.length;

  return (
    <Box>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 4, gap: 2, flexWrap: 'wrap' }}>
        <Typography variant="h4" sx={{ fontWeight: 700 }}>
          Contact Submissions
        </Typography>
        <ButtonPill
          size="small"
          variant="outlined"
          startIcon={<FileDownload />}
          disabled={exporting}
          onClick={handleExport}
        >
          Export CSV
        </ButtonPill>
      </Box>

      {selected.length > 0 && (
        <Paper sx={{ ...panelSx(theme), mb: 3, display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
          <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>
            {selected.length} selected
          </Typography>
          <Box sx={{ flex: 1 }} />
          <ButtonPill
            size="small"
            color="success"
            startIcon={<ResolveIcon />}
            disabled={bulkLoading}
            onClick={() => runBulk('set_status', 'resolved')}
          >
            Mark resolved
          </ButtonPill>
          <ButtonPill
            size="small"
            variant="outlined"
            startIcon={<CloseIcon />}
            disabled={bulkLoading}
            onClick={() => runBulk('set_status', 'closed')}
          >
            Mark closed
          </ButtonPill>
          <ButtonPill
            size="small"
            variant="outlined"
            color="error"
            startIcon={<DeleteIcon />}
            disabled={bulkLoading}
            onClick={() => setConfirmDeleteOpen(true)}
          >
            Delete
          </ButtonPill>
        </Paper>
      )}

      <TableContainer component={Paper}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
        <Table>
          <TableHead>
            <TableRow sx={{ bgcolor: theme.palette.grey[50] }}>
              <TableCell padding="checkbox">
                <Checkbox
                  checked={allSelected}
                  indeterminate={someSelected}
                  onChange={handleSelectAll}
                  inputProps={{ 'aria-label': 'select all contacts' }}
                />
              </TableCell>
              <TableCell>Name</TableCell>
              <TableCell>Subject</TableCell>
              <TableCell>Email</TableCell>
              <TableCell>Date</TableCell>
              <TableCell align="center">Status</TableCell>
              <TableCell align="center">Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {contacts.map((contact) => {
              const isSelected = selected.includes(contact.id);
              return (
              <TableRow key={contact.id} hover selected={isSelected}>
                <TableCell padding="checkbox">
                  <Checkbox
                    checked={isSelected}
                    onChange={() => toggleSelect(contact.id)}
                    inputProps={{ 'aria-label': `select contact ${contact.id}` }}
                  />
                </TableCell>
                <TableCell>{contact.name}</TableCell>
                <TableCell>{contact.subject}</TableCell>
                <TableCell>{contact.email}</TableCell>
                <TableCell>{(contact.created_at || contact.createdAt) ? new Date(contact.created_at || contact.createdAt).toLocaleDateString() : '-'}</TableCell>
                <TableCell align="center">
                  <Chip label={contact.status} color={getStatusColor(contact.status)} size="small" />
                </TableCell>
                <TableCell align="center">
                  <IconButton size="small" onClick={() => handleViewDetails(contact)}>
                    <ViewIcon />
                  </IconButton>
                </TableCell>
              </TableRow>
            );
            })}
          </TableBody>
        </Table>
        )}
        <TablePagination
          component="div"
          count={totalCount}
          page={page}
          onPageChange={(e, newPage) => setPage(newPage)}
          rowsPerPage={rowsPerPage}
          onRowsPerPageChange={(e) => setRowsPerPage(parseInt(e.target.value, 10))}
        />
      </TableContainer>

      <Dialog open={confirmDeleteOpen} onClose={() => setConfirmDeleteOpen(false)}>
        <DialogTitle>Delete contacts</DialogTitle>
        <DialogContent>
          <DialogContentText>
            Delete {selected.length} selected contact(s)? This cannot be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setConfirmDeleteOpen(false)} disabled={bulkLoading}>Cancel</Button>
          <Button color="error" variant="contained" onClick={() => runBulk('delete')} disabled={bulkLoading}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={openDialog} onClose={() => setOpenDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>Contact Submission</DialogTitle>
        <DialogContent>
          {selectedContact && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12}>
                <TextField fullWidth label="Name" value={selectedContact.name} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Email" value={selectedContact.email} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField fullWidth label="Phone" value={selectedContact.phone || 'N/A'} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth label="Subject" value={selectedContact.subject} InputProps={{ readOnly: true }} />
              </Grid>
              <Grid item xs={12}>
                <TextField fullWidth multiline rows={4} label="Message" value={selectedContact.message} InputProps={{ readOnly: true }} />
              </Grid>
              {selectedContact.response && (
                <Grid item xs={12}>
                  <TextField fullWidth multiline rows={3} label="Response" value={selectedContact.response} InputProps={{ readOnly: true }} />
                </Grid>
              )}
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setOpenDialog(false)}>Close</Button>
          <Button variant="contained">Reply</Button>
        </DialogActions>
      </Dialog>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={3500}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert severity={snackbar.severity} variant="filled" onClose={() => setSnackbar((s) => ({ ...s, open: false }))} sx={{ borderRadius: 2 }}>
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default ContactManagement;
